import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { initGA, trackPageView, reportWebVitals } from './utils/analytics';
import { hasAnalyticsConsent } from './utils/cookieConsent';
import { DashboardSkeleton, ParticipantSkeleton } from './components/SkeletonLoading';
import Layout from './components/Layout';

const LazyCookieConsent = lazy(() => import('./components/CookieConsent'));

// Lazy loading des pages pour optimiser les performances
const Landing = lazy(() => import('./pages/Landing'));
const Organizer = lazy(() => import('./pages/Organizer'));
const Participant = lazy(() => import('./pages/Participant'));
const Admin = lazy(() => import('./pages/Admin'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Analytics = lazy(() => import('./pages/Analytics'));
const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const Success = lazy(() => import('./pages/Success'));
const Cancel = lazy(() => import('./pages/Cancel'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const NotFound = lazy(() => import('./pages/NotFound'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const CGV = lazy(() => import('./pages/CGV'));
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'));
const Contact = lazy(() => import('./pages/Contact'));
const PriseRendezVous = lazy(() => import('./pages/PriseRendezVous'));
const SondageDisponibilites = lazy(() => import('./pages/SondageDisponibilites'));
const PlanifierSortie = lazy(() => import('./pages/PlanifierSortie'));
const AlternativeDoodle = lazy(() => import('./pages/AlternativeDoodle'));
const AlternativeFramadate = lazy(() => import('./pages/AlternativeFramadate'));
const AlternativeRallly = lazy(() => import('./pages/AlternativeRallly'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogOrganiserEvenement = lazy(() => import('./pages/BlogArticle1'));
const BlogDoodleVsGetsynkro = lazy(() => import('./pages/BlogArticle2'));
const BlogMeilleuresApps = lazy(() => import('./pages/BlogArticle3'));
const Blog7AlternativesDoodle = lazy(() => import('./pages/BlogArticle4'));
const PlanifierRendezVousGroupe = lazy(() => import('./pages/PlanifierRendezVousGroupe'));
const OutilSondageGratuit = lazy(() => import('./pages/OutilSondageGratuit'));
const AlternativeCalendly = lazy(() => import('./pages/AlternativeCalendly'));
const SansCreationDeCompte = lazy(() => import('./pages/SansCreationDeCompte'));
const FreelanceIndependant = lazy(() => import('./pages/FreelanceIndependant'));

// GA4 initialized only after cookie consent — see CookieConsent component
// If user already consented in a previous session, init immediately
if (hasAnalyticsConsent()) {
  initGA();
  reportWebVitals();
}

// Component to track page views
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
};

// Composant de chargement minimal pendant le lazy loading (pages génériques)
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F9FAFB',
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #E5E7EB',
      borderTopColor: '#6D28D9',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const DeferredCookieConsent = ({ onConsent }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const id = schedule(() => setReady(true));
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <LazyCookieConsent onConsent={onConsent} />
    </Suspense>
  );
};

function App() {
  const handleCookieConsent = (consent) => {
    if (consent.analytics) {
      initGA();
      trackPageView(window.location.pathname, document.title);
      reportWebVitals();
    }
  };

  return (
    <ErrorBoundary>
      <UserProvider>
        <Router>
          {/* Skip-to-content link for accessibility */}
          <a href="#main-content" className="skip-link">
            Passer au contenu principal
          </a>
          <ScrollToTop />
          <PageViewTracker />
          <DeferredCookieConsent onConsent={handleCookieConsent} />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Auth pages — full-screen Clerk design, no Layout wrapper */}
              <Route path="/sign-in/*" element={<SignIn />} />
              <Route path="/sign-up/*" element={<SignUp />} />

              {/* All other pages — global sticky Header via Layout */}
              <Route path="/" element={<Layout><Landing /></Layout>} />
              <Route path="/organizer" element={<Layout><Organizer /></Layout>} />
              <Route path="/create" element={<Layout><Organizer /></Layout>} />
              <Route path="/participant" element={<Layout><Suspense fallback={<ParticipantSkeleton />}><Participant /></Suspense></Layout>} />
              <Route path="/admin" element={<Layout><Admin /></Layout>} />
              <Route path="/admin/:eventId" element={<Layout><Admin /></Layout>} />
              <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
              <Route path="/success" element={<Layout><Success /></Layout>} />
              <Route path="/cancel" element={<Layout><Cancel /></Layout>} />
              <Route path="/dashboard" element={<ProtectedRoute><Layout><Suspense fallback={<DashboardSkeleton />}><Dashboard /></Suspense></Layout></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><Layout><Analytics /></Layout></ProtectedRoute>} />
              <Route path="/accept-invitation" element={<Layout><AcceptInvitation /></Layout>} />
              {/* Legal pages - accessible sans authentification */}
              <Route path="/mentions-legales" element={<Layout><MentionsLegales /></Layout>} />
              <Route path="/cgv" element={<Layout><CGV /></Layout>} />
              <Route path="/confidentialite" element={<Layout><PolitiqueConfidentialite /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              {/* SEO Landing Pages */}
              <Route path="/prise-de-rendez-vous-en-ligne" element={<Layout><PriseRendezVous /></Layout>} />
              <Route path="/sondage-disponibilites" element={<Layout><SondageDisponibilites /></Layout>} />
              <Route path="/planifier-sortie-entre-amis" element={<Layout><PlanifierSortie /></Layout>} />
              <Route path="/alternative-doodle" element={<Layout><AlternativeDoodle /></Layout>} />
              {/* Placeholder redirects — pages dédiées à créer plus tard */}
              <Route path="/alternative-when2meet" element={<Navigate to="/alternative-doodle" replace />} />
              <Route path="/alternative-lettucemeet" element={<Navigate to="/alternative-doodle" replace />} />
              <Route path="/planifier-rendez-vous-groupe" element={<Layout><PlanifierRendezVousGroupe /></Layout>} />
              <Route path="/outil-sondage-gratuit" element={<Layout><OutilSondageGratuit /></Layout>} />
              <Route path="/alternatives/framadate" element={<Layout><AlternativeFramadate /></Layout>} />
              <Route path="/alternatives/rallly" element={<Layout><AlternativeRallly /></Layout>} />
              <Route path="/alternatives/calendly" element={<Layout><AlternativeCalendly /></Layout>} />
              <Route path="/fonctionnalites/sans-creation-de-compte" element={<Layout><SansCreationDeCompte /></Layout>} />
              <Route path="/cas-usage/freelance-independant" element={<Layout><FreelanceIndependant /></Layout>} />
              {/* Blog */}
              <Route path="/blog" element={<Layout><Blog /></Layout>} />
              <Route path="/blog/organiser-evenement-groupe-sans-stress" element={<Layout><BlogOrganiserEvenement /></Layout>} />
              <Route path="/blog/doodle-vs-getsynkro-comparatif" element={<Layout><BlogDoodleVsGetsynkro /></Layout>} />
              <Route path="/blog/meilleures-applications-planifier-sortie-entre-amis" element={<Layout><BlogMeilleuresApps /></Layout>} />
              <Route path="/blog/7-alternatives-doodle-2026" element={<Layout><Blog7AlternativesDoodle /></Layout>} />
              {/* Route 404 - doit être en dernier */}
              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </Suspense>
        </Router>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;
